import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// web/src/rendering/components/leaves/DataTable.tsx
// Financial-grade tabular data renderer.
// Backend emits: {component: "data_table", props: {title, columns: [{key, label, align?, type?}], rows: [...]}}
// type: "currency" | "percent" | "bps" | "number" | "text" (default)
import { memo } from "react";
import { useLocaleStore } from "../../../stores/localeStore";
import { fmtCurrency, fmtNumber, fmtPercent } from "../../../lib/formatters";
const ALIGN = {
    left: "text-left",
    right: "text-right",
    center: "text-center",
};
// CR-2026-05-24-001 Batch 8: numeric columns auto-right-align when the
// backend hasn't explicitly set `align`. Text columns default to left.
const NUMERIC_TYPES = new Set(["currency", "percent", "bps", "number"]);
function alignFor(col) {
    if (col.align)
        return ALIGN[col.align];
    return col.type && NUMERIC_TYPES.has(col.type) ? ALIGN.right : ALIGN.left;
}
function formatValue(raw, type, currency, locale) {
    if (raw === null || raw === undefined || raw === "") {
        return { text: "—", tone: "neutral" };
    }
    const num = typeof raw === "number" ? raw : parseFloat(String(raw).replace(/[^0-9.\-]/g, ""));
    const isNumeric = !isNaN(num);
    switch (type) {
        case "currency": {
            if (!isNumeric)
                return { text: String(raw), tone: "neutral" };
            return {
                text: fmtCurrency(num, locale, currency),
                tone: num > 0 ? "positive" : num < 0 ? "negative" : "neutral",
            };
        }
        case "percent": {
            if (!isNumeric)
                return { text: String(raw), tone: "neutral" };
            return {
                text: fmtPercent(num, locale),
                tone: num > 0 ? "positive" : num < 0 ? "negative" : "neutral",
            };
        }
        case "bps": {
            if (!isNumeric)
                return { text: String(raw), tone: "neutral" };
            const sign = num > 0 ? "+" : "";
            return { text: `${sign}${num} bps`, tone: num > 0 ? "positive" : num < 0 ? "negative" : "neutral" };
        }
        case "number": {
            if (!isNumeric)
                return { text: String(raw), tone: "neutral" };
            return { text: fmtNumber(num, locale), tone: "neutral" };
        }
        default:
            return { text: String(raw), tone: "neutral" };
    }
}
const TONE_CLASS = {
    positive: "text-emerald-400",
    negative: "text-rose-400",
    neutral: "text-slate-300",
};
function DataTableImpl({ title, columns = [], rows = [], currency, groups, group_by }) {
    const locale = useLocaleStore((s) => s.locale);
    const defaultCurrency = useLocaleStore((s) => s.currency);
    const resolvedCurrency = currency ?? defaultCurrency;
    const primaryCol = columns.find((c) => c.primary) ?? columns[0];
    // Build groups from `group_by` column if no explicit groups were passed.
    let renderGroups = null;
    if (groups && groups.length > 0) {
        renderGroups = groups;
    }
    else if (group_by) {
        const buckets = new Map();
        for (const row of rows) {
            const key = String(row[group_by] ?? "—");
            if (!buckets.has(key))
                buckets.set(key, []);
            buckets.get(key).push(row);
        }
        renderGroups = Array.from(buckets.entries()).map(([name, rs]) => ({ name, rows: rs }));
    }
    return (_jsxs("div", { className: "rounded-xl border border-slate-700 bg-slate-900 overflow-hidden", children: [title && (_jsx("div", { className: "px-4 py-3 border-b border-slate-700", children: _jsx("p", { className: "text-sm font-medium text-slate-200", children: title }) })), _jsx("div", { className: `hidden md:block overflow-x-auto ${rows.length > 12 ? "max-h-[28rem] overflow-y-auto" : ""}`, children: _jsxs("table", { className: "w-full text-sm", children: [_jsx("thead", { children: _jsx("tr", { className: "border-b border-slate-800", children: columns.map((col) => (_jsx("th", { className: `px-4 py-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500 ${alignFor(col)}`, children: col.label }, col.key))) }) }), _jsx("tbody", { children: renderGroups
                                ? renderGroups.flatMap((g, gi) => [
                                    _jsx("tr", { className: "bg-slate-800/40 border-y border-slate-700", children: _jsxs("td", { colSpan: columns.length, className: "px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-300", children: [g.name, typeof g.subtotal === "number" && (_jsxs("span", { className: "ml-2 text-slate-500 normal-case font-normal", children: ["\u00B7 ", fmtCurrency(g.subtotal, locale, resolvedCurrency)] }))] }) }, `gh-${gi}`),
                                    ...g.rows.map((row, ri) => (_jsx("tr", { className: "border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors last:border-0", children: columns.map((col) => {
                                            const { text, tone } = formatValue(row[col.key], col.type, resolvedCurrency, locale);
                                            return (_jsx("td", { className: `px-4 py-2 tabular-nums ${alignFor(col)} ${TONE_CLASS[tone]}`, children: text }, col.key));
                                        }) }, `g-${gi}-r-${ri}`))),
                                ])
                                : rows.map((row, ri) => (_jsx("tr", { className: "border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors last:border-0", children: columns.map((col) => {
                                        const { text, tone } = formatValue(row[col.key], col.type, resolvedCurrency, locale);
                                        return (_jsx("td", { className: `px-4 py-2 tabular-nums ${alignFor(col)} ${TONE_CLASS[tone]}`, children: text }, col.key));
                                    }) }, ri))) })] }) }), _jsx("div", { className: "md:hidden divide-y divide-slate-800/50", children: rows.map((row, ri) => {
                    const { text: primaryText } = formatValue(row[primaryCol?.key], primaryCol?.type, resolvedCurrency, locale);
                    const secondaryCols = columns.filter((c) => c !== primaryCol);
                    return (_jsxs("div", { className: "px-4 py-3 space-y-2", children: [_jsx("span", { className: "text-sm font-medium text-slate-200", children: primaryText }), _jsx("div", { className: "grid grid-cols-2 gap-x-4 gap-y-1", children: secondaryCols.map((col) => {
                                    const { text, tone } = formatValue(row[col.key], col.type, resolvedCurrency, locale);
                                    return (_jsxs("div", { children: [_jsx("p", { className: "text-[10px] uppercase tracking-wide text-slate-500", children: col.label }), _jsx("p", { className: `text-sm tabular-nums ${TONE_CLASS[tone]}`, children: text })] }, col.key));
                                }) })] }, ri));
                }) })] }));
}
// Memoised — see PieChart for rationale.
export const DataTable = memo(DataTableImpl);
